import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { checkFirstLevelApproverPermission } from "services/utils/HelperFunction";
import { useSelector } from "react-redux";
import { themes, tableRowSx } from "services/constants";
import { CustomTextBox } from "components/CustomComponents/CustomTextBox";
import { showToast } from "services/utils/Status";
// rowEndStyle
import {
  formBackGroundColor,
  tooltipStyleProps,
} from "services/constants/Styles";
import {
  EligibleMetEmployeeHeader,
  EligibleMetEmployeeCustomHeader,
  RemovedEmployeeHeader,
} from "../../Headers";
import {
  ArrowUpward,
  Cancel,
  Done,
  Edit,
  ArrowDownward,
} from "@mui/icons-material";
import { CustomButtonWithOutLoader } from "components/CustomComponents/CustomButton";

export const EligibleMetEmployee = ({
  eligibleMetEmployee,
  setEligibleMetEmployee,
  removedEmployees,
  setRemovedEmployees,
  IncentiveType,
}) => {
  const { userScreenAccess } = useSelector((state) => state.tokenReducer);
  // const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectRemoveRows, setSelectRemoveRows] = useState(new Set());
  const [selectRestoreRows, setSelectRestoreRows] = useState(new Set());
  const containerRef = useRef(null);
  const [removeSelectAll, setRemoveSelectAll] = useState(false);
  const [restoreSelectAll, setRestoreSelectAll] = useState(false);
  const [editRowId, setEditRowId] = useState(null);
  const [tempAmount, setTempAmount] = useState({});
  const [amtError, setAmtError] = useState(false);

  // const [listViewPage, setListViewPage] = useState(0);

  useEffect(() => {
    if (eligibleMetEmployee?.length !== 0) {
      const allIds = eligibleMetEmployee?.map((row) => row.user);
      const allSelected =
        allIds?.length > 0 &&
        allIds.every((user) => selectRemoveRows.has(user));

      setRemoveSelectAll(allSelected);
    }
  }, [selectRemoveRows, setRemoveSelectAll, eligibleMetEmployee]);

  useEffect(() => {
    if (removedEmployees?.length !== 0) {
      const allIds = removedEmployees?.map((row) => row.user);
      const allSelected =
        allIds?.length > 0 &&
        allIds.every((user) => selectRestoreRows.has(user));

      setRestoreSelectAll(allSelected);
    }
  }, [
    selectRemoveRows,
    setRemoveSelectAll,
    removedEmployees,
    selectRestoreRows,
  ]);

  // const handleChangePage = (event, newPage) => {
  //   setListViewPage(newPage);
  // };

  // const handleChangeRowsPerPage = (event) => {
  //   setRowsPerPage(parseInt(event.target.value, 10));
  //   setListViewPage(0);
  // };

  const selectionRemove = () => {
    setRemoveSelectAll(false);
    setRestoreSelectAll(false);
    setSelectRestoreRows(new Set());
    setSelectRemoveRows(new Set());
  };

  const handleRemove = (event) => {
    const checked = event.target.checked;

    if (checked) {
      const allIds = eligibleMetEmployee?.map((row) => row.user);
      setSelectRemoveRows(new Set(allIds));
    } else {
      setSelectRemoveRows(new Set());
    }
    setRemoveSelectAll(checked);
  };

  const handleRestore = (event) => {
    const checked = event.target.checked;
    if (checked) {
      const allIds = removedEmployees?.map((row) => row.user);
      setSelectRestoreRows(new Set(allIds));
    } else {
      setSelectRestoreRows(new Set());
    }
    setRestoreSelectAll(checked);
  };

  const handleRemoveRowSelect = (rowId) => {
    setSelectRemoveRows((prev) => {
      const updated = new Set(prev);

      if (updated.has(rowId)) {
        updated.delete(rowId);
      } else {
        updated.add(rowId);
      }

      return updated;
    });
  };

  const handleRestoreRowSelect = (rowId) => {
    setSelectRestoreRows((prev) => {
      const updated = new Set(prev);

      if (updated.has(rowId)) {
        updated.delete(rowId);
      } else {
        updated.add(rowId);
      }

      return updated;
    });
  };

  // useEffect(() => {
  //   scrollTop(containerRef);
  // }, [listViewPage]);

  const handleBulkRemove = () => {
    if (selectRemoveRows?.size !== 0) {
      const selectedIds = Array.isArray(selectRemoveRows)
        ? selectRemoveRows
        : Array.from(selectRemoveRows);
      const toRemove = eligibleMetEmployee.filter((emp) =>
        selectedIds.includes(emp.user)
      );

      setEligibleMetEmployee((prev) =>
        prev.filter((emp) => !selectedIds.includes(emp.user))
      );

      setRemovedEmployees((prev) => [...prev, ...toRemove]);
      selectionRemove();
      showToast("error", "Employee removed successfully");
    }
  };

  const handleBulkAdd = () => {
    if (selectRestoreRows?.size !== 0) {
      const selectedIds = Array.isArray(selectRestoreRows)
        ? selectRestoreRows
        : Array.from(selectRestoreRows);
      const toRestore = removedEmployees.filter((emp) =>
        selectedIds.includes(emp.user)
      );

      setRemovedEmployees((prev) =>
        prev.filter((emp) => !selectedIds.includes(emp.user))
      );

      setEligibleMetEmployee((prev) => [...prev, ...toRestore]);
      selectionRemove();
      showToast("success", "Employee added successfully");
    }
  };

  const updateTempAmount = (userId, value) => {
    if (value?.length <= 13 && /^$|^\d{0,10}(\.\d{0,2})?$/.test(value)) {
      setTempAmount((prev) => ({
        ...prev,
        [userId]: value,
      }));
    }
  };

  const handleAmountChange = (userId) => {
    const value = tempAmount[userId];
    if (value === "" || value === null || value === undefined) {
      setAmtError(true);
      return;
    }
    setEligibleMetEmployee((prev) =>
      prev.map((emp) =>
        emp.user === userId
          ? { ...emp, incentive_amount: tempAmount[userId] }
          : emp
      )
    );
    setEditRowId(null);
    setAmtError(false);
    showToast("success", "Amount updated successfully");
  };

  return (
    <React.Fragment>
      {IncentiveType !== "Custom" ? (
        <>
          <Mui.TableContainer
            component={Mui.Paper}
            ref={containerRef}
            sx={{
              width: "100%",
              minHeight: 330,
              maxHeight: 330,
              ml: 2,
              mr: 2,
              mt: 2,
            }}
          >
            <Mui.Table className="border">
              <Mui.TableHead
                sx={{
                  backgroundColor: themes.primary,
                  position: "sticky",
                  top: -1,
                  zIndex: 10,
                }}
              >
                <Mui.TableRow sx={tableRowSx}>
                  {EligibleMetEmployeeHeader.map((header) => (
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        textAlign: header.name === "Action" ? "center" : "left",
                      }}
                    >
                      {header.key === "checkbox" ? (
                        <Mui.Checkbox
                          checked={removeSelectAll}
                          onChange={(event) => {
                            if (
                              checkFirstLevelApproverPermission(
                                userScreenAccess?.module_details,
                                "Approve",
                                "Over Time"
                              )
                            ) {
                              handleRemove(event);
                            } else {
                              showToast(
                                "error",
                                "Access denied: You are not authorized to approve or  reject the overtime"
                              );
                            }
                          }}
                          size="medium"
                          // disabled={
                          //   eligibleMetEmployee?.employees?.length === 0 ||
                          //   eligibleMetEmployee?.employees?.length === 1
                          // }
                        />
                      ) : header.name !== "Arrow" ? (
                        header.name
                      ) : null}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {eligibleMetEmployee?.length === 0 ? (
                  <Mui.TableRow sx={tableRowSx}>
                    <Mui.TableCell colSpan={12}>
                      <Mui.Alert severity="info" sx={{ margin: 2 }}>
                        No data available
                      </Mui.Alert>
                    </Mui.TableCell>
                  </Mui.TableRow>
                ) : (
                  eligibleMetEmployee
                    // ?.slice(
                    //   listViewPage * rowsPerPage,
                    //   listViewPage * rowsPerPage + rowsPerPage
                    // )
                    .map((eligible_employee) => (
                      <>
                        <Mui.TableRow
                          key={eligible_employee.user}
                          sx={tableRowSx}
                        >
                          {EligibleMetEmployeeHeader.map((header) =>
                            header.name === "" ? (
                              <Mui.TableCell sx={{ width: "1%" }}>
                                <Mui.Checkbox
                                  checked={selectRemoveRows.has(
                                    eligible_employee.user
                                  )}
                                  onChange={() => {
                                    if (
                                      checkFirstLevelApproverPermission(
                                        userScreenAccess?.module_details,
                                        "Approve",
                                        "Over Time"
                                      )
                                    ) {
                                      handleRemoveRowSelect(
                                        eligible_employee.user
                                      );
                                    } else {
                                      showToast(
                                        "error",
                                        "Access denied: You are not authorized to approve or  reject the overtime"
                                      );
                                    }
                                  }}
                                />
                              </Mui.TableCell>
                            ) : header.name !== "Action" &&
                              header.name !== "Arrow" ? (
                              <Mui.TableCell sx={{ color: themes.headLine }}>
                                {header.name === "Name" ? (
                                  <Mui.Tooltip
                                    title={eligible_employee?.employee_name}
                                    arrow
                                    slotProps={tooltipStyleProps}
                                  >
                                    <Mui.Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      maxWidth={120}
                                    >
                                      <Mui.Avatar
                                        alt={eligible_employee?.employee_name
                                          ?.charAt(0)
                                          .toUpperCase()}
                                        src={eligible_employee?.image_url}
                                        sx={{ width: 23, height: 23 }}
                                      />
                                      <span
                                        style={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "block",
                                        }}
                                      >
                                        {eligible_employee?.employee_name}
                                      </span>
                                    </Mui.Box>
                                  </Mui.Tooltip>
                                ) : eligible_employee[header.key] ||
                                  eligible_employee[header.key] === 0 ? (
                                  String(eligible_employee[header.key]).length >
                                  10 ? (
                                    <Mui.Tooltip
                                      title={eligible_employee[header.key]}
                                      arrow
                                      slotProps={tooltipStyleProps}
                                    >
                                      <span
                                        style={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          display: "inline-block",
                                          maxWidth: 80,
                                        }}
                                      >
                                        {eligible_employee[header.key]}
                                      </span>
                                    </Mui.Tooltip>
                                  ) : (
                                    eligible_employee[header.key]
                                  )
                                ) : (
                                  "N/A"
                                )}
                              </Mui.TableCell>
                            ) : null
                          )}
                        </Mui.TableRow>
                      </>
                    ))
                )}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
          <Mui.Grid
            container
            sx={{
              fisplay: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 0.2,
            }}
            spacing={2}
          >
            <Mui.Grid item>
              <CustomButtonWithOutLoader
                actionFuntion={() => {
                  handleBulkAdd();
                }}
                actionName="Add"
                typeName="submit"
                customWidth={true}
                disableAction={selectRemoveRows?.size}
                AddIcon={<ArrowUpward fontSize="small" />}
              />
            </Mui.Grid>
            <Mui.Grid item>
              <CustomButtonWithOutLoader
                actionFuntion={() => {
                  handleBulkRemove();
                }}
                disableAction={selectRestoreRows?.size}
                actionName="Remove"
                typeName="submit"
                customWidth={true}
                customTitleSx={{
                  backgroundColor: themes.redColor,
                  color: themes?.white,
                  "&:hover": {
                    backgroundColor: themes.redColor,
                  },
                }}
                AddIcon={<ArrowDownward fontSize="small" />}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid container sx={{ mr: 2, ml: 2 }}>
            <Mui.Grid item xs={12}>
              <Mui.Grid
                item
                sx={{
                  mt: 2,
                  border: `2px solid ${themes.sidebarDivider}`,
                  backgroundColor: formBackGroundColor,
                  padding: 1,
                  borderRadius: 1,
                }}
              >
                <Mui.Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
                  {`Incentive Removed Employees : ${removedEmployees?.length}`}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Grid>

          <Mui.TableContainer
            ref={containerRef}
            sx={{
              width: "100%",
              minHeight: 330,
              maxHeight: 330,
              ml: 2,
              mr: 2,
              mt: 2,
            }}
            component={Mui.Paper}
          >
            <Mui.Table className="border">
              <Mui.TableHead
                sx={{
                  backgroundColor: themes.primary,
                  position: "sticky",
                  top: -1,
                  zIndex: 10,
                }}
              >
                <Mui.TableRow sx={tableRowSx}>
                  {EligibleMetEmployeeHeader.map((header) => (
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        textAlign: header.name === "Action" ? "center" : "left",
                      }}
                    >
                      {header.key === "checkbox" ? (
                        <Mui.Checkbox
                          checked={restoreSelectAll}
                          onChange={(event) => {
                            if (
                              checkFirstLevelApproverPermission(
                                userScreenAccess?.module_details,
                                "Approve",
                                "Over Time"
                              )
                            ) {
                              handleRestore(event);
                            } else {
                              showToast(
                                "error",
                                "Access denied: You are not authorized to approve or  reject the overtime"
                              );
                            }
                          }}
                          size="medium"
                          // disabled={
                          //   eligibleMetEmployee?.employees?.length === 0 ||
                          //   eligibleMetEmployee?.employees?.length === 1
                          // }
                        />
                      ) : header.name !== "Arrow" ? (
                        header.name
                      ) : null}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {removedEmployees?.length === 0 ? (
                  <Mui.TableRow sx={tableRowSx}>
                    <Mui.TableCell colSpan={12}>
                      <Mui.Alert severity="info" sx={{ margin: 2 }}>
                        No data available
                      </Mui.Alert>
                    </Mui.TableCell>
                  </Mui.TableRow>
                ) : (
                  removedEmployees
                    // ?.slice(
                    //   listViewPage * rowsPerPage,
                    //   listViewPage * rowsPerPage + rowsPerPage
                    // )
                    .map((eligible_employee) => (
                      <>
                        <Mui.TableRow
                          key={eligible_employee?.user}
                          sx={tableRowSx}
                        >
                          {EligibleMetEmployeeHeader.map((header) =>
                            header.name === "" ? (
                              <Mui.TableCell sx={{ width: "1%" }}>
                                <Mui.Checkbox
                                  checked={selectRestoreRows.has(
                                    eligible_employee?.user
                                  )}
                                  onChange={() => {
                                    if (
                                      checkFirstLevelApproverPermission(
                                        userScreenAccess?.module_details,
                                        "Approve",
                                        "Over Time"
                                      )
                                    ) {
                                      handleRestoreRowSelect(
                                        eligible_employee?.user
                                      );
                                    } else {
                                      showToast(
                                        "error",
                                        "Access denied: You are not authorized to approve or  reject the overtime"
                                      );
                                    }
                                  }}
                                />
                              </Mui.TableCell>
                            ) : header.name !== "Action" &&
                              header.name !== "Arrow" ? (
                              <Mui.TableCell sx={{ color: themes.headLine }}>
                                {header.name === "Name" ? (
                                  <Mui.Tooltip
                                    title={eligible_employee?.employee_name}
                                    arrow
                                    slotProps={tooltipStyleProps}
                                  >
                                    <Mui.Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      maxWidth={120}
                                    >
                                      <Mui.Avatar
                                        alt={eligible_employee?.employee_name
                                          ?.charAt(0)
                                          .toUpperCase()}
                                        src={eligible_employee?.image_url}
                                        sx={{ width: 23, height: 23 }}
                                      />
                                      <span
                                        style={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "block",
                                        }}
                                      >
                                        {eligible_employee?.employee_name}
                                      </span>
                                    </Mui.Box>
                                  </Mui.Tooltip>
                                ) : eligible_employee[header.key] ||
                                  eligible_employee[header.key] === 0 ? (
                                  String(eligible_employee[header.key]).length >
                                  10 ? (
                                    <Mui.Tooltip
                                      title={eligible_employee[header.key]}
                                      arrow
                                      slotProps={tooltipStyleProps}
                                    >
                                      <span
                                        style={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          display: "inline-block",
                                          maxWidth: 80,
                                        }}
                                      >
                                        {eligible_employee[header.key]}
                                      </span>
                                    </Mui.Tooltip>
                                  ) : (
                                    eligible_employee[header.key]
                                  )
                                ) : (
                                  "N/A"
                                )}
                              </Mui.TableCell>
                            ) : null
                          )}
                        </Mui.TableRow>
                      </>
                    ))
                )}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
        </>
      ) : (
        <>
          <Mui.TableContainer
            ref={containerRef}
            component={Mui.Paper}
            sx={{
              width: "100%",
              minHeight: 330,
              maxHeight: 330,
              ml: 2,
              mr: 2,
              mt: 2,
            }}
          >
            <Mui.Table className="border">
              <Mui.TableHead
                sx={{
                  backgroundColor: themes.primary,
                  position: "sticky",
                  top: -1,
                  zIndex: 10,
                }}
              >
                <Mui.TableRow sx={tableRowSx}>
                  {EligibleMetEmployeeCustomHeader.map((header) => (
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        textAlign: "left",
                        width: header.key === "checkbox" ? "2%" : "30%",
                      }}
                    >
                      {header.key === "checkbox" ? (
                        <Mui.Checkbox
                          checked={removeSelectAll}
                          onChange={(event) => {
                            if (
                              checkFirstLevelApproverPermission(
                                userScreenAccess?.module_details,
                                "Approve",
                                "Over Time"
                              )
                            ) {
                              handleRemove(event);
                            } else {
                              showToast(
                                "error",
                                "Access denied: You are not authorized to approve or  reject the overtime"
                              );
                            }
                          }}
                          size="medium"
                          // disabled={
                          //   eligibleMetEmployee?.employees?.length === 0 ||
                          //   eligibleMetEmployee?.employees?.length === 1
                          // }
                        />
                      ) : header.name !== "Arrow" ? (
                        header.name
                      ) : null}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {eligibleMetEmployee?.length === 0 ? (
                  <Mui.TableRow sx={tableRowSx}>
                    <Mui.TableCell colSpan={12}>
                      <Mui.Alert severity="info" sx={{ margin: 2 }}>
                        No data available
                      </Mui.Alert>
                    </Mui.TableCell>
                  </Mui.TableRow>
                ) : (
                  eligibleMetEmployee
                    // ?.slice(
                    //   listViewPage * rowsPerPage,
                    //   listViewPage * rowsPerPage + rowsPerPage
                    // )
                    .map((eligible_employee) => (
                      <>
                        <Mui.TableRow
                          key={eligible_employee.user}
                          sx={tableRowSx}
                        >
                          {EligibleMetEmployeeCustomHeader.map((header) =>
                            header.name === "" ? (
                              <Mui.TableCell sx={{ width: "1%" }}>
                                <Mui.Checkbox
                                  checked={selectRemoveRows.has(
                                    eligible_employee.user
                                  )}
                                  onChange={() => {
                                    if (
                                      checkFirstLevelApproverPermission(
                                        userScreenAccess?.module_details,
                                        "Approve",
                                        "Over Time"
                                      )
                                    ) {
                                      handleRemoveRowSelect(
                                        eligible_employee.user
                                      );
                                    } else {
                                      showToast(
                                        "error",
                                        "Access denied: You are not authorized to approve or  reject the overtime"
                                      );
                                    }
                                  }}
                                />
                              </Mui.TableCell>
                            ) : header.name !== "Arrow" ? (
                              <Mui.TableCell
                                sx={{
                                  color: themes.headLine,
                                  width:
                                    header.key === "checkbox" ? "2%" : "30%",
                                }}
                              >
                                {header.name === "Action" ? (
                                  editRowId === eligible_employee.user ? (
                                    <>
                                      <Mui.IconButton
                                        size="small"
                                        onClick={() =>
                                          handleAmountChange(
                                            eligible_employee.user
                                          )
                                        }
                                      >
                                        <Done
                                          sx={{
                                            color: themes.primaryIndicator,
                                          }}
                                        />
                                      </Mui.IconButton>
                                      <Mui.IconButton
                                        size="small"
                                        onClick={() => {
                                          setEditRowId(null);
                                          setAmtError(false);
                                          setTempAmount(
                                            tempAmount[
                                              eligible_employee.user
                                            ] ??
                                              eligible_employee.incentive_amount ??
                                              ""
                                          );
                                        }}
                                      >
                                        <Cancel
                                          sx={{ color: themes.redColor }}
                                        />
                                      </Mui.IconButton>
                                    </>
                                  ) : (
                                    <Mui.IconButton
                                      size="small"
                                      onClick={() => {
                                        setEditRowId(eligible_employee.user);
                                      }}
                                    >
                                      <Edit
                                        sx={{ color: themes.primaryIndicator }}
                                      />
                                    </Mui.IconButton>
                                  )
                                ) : header.key === "incentive_amount" &&
                                  editRowId === eligible_employee.user ? (
                                  <>
                                    <CustomTextBox
                                      selectedValue={
                                        tempAmount[eligible_employee.user] ??
                                        eligible_employee.incentive_amount ??
                                        ""
                                      }
                                      updateDataFunction={(e) =>
                                        updateTempAmount(
                                          eligible_employee.user,
                                          e.target.value
                                        )
                                      }
                                      placeHolder="Enter amount"
                                    />
                                    {amtError ? (
                                      <h6
                                        style={{
                                          padding: 7,
                                          color: themes.helperTextColor,
                                          fontSize: 11.5,
                                        }}
                                      >
                                        Amount required
                                      </h6>
                                    ) : null}
                                  </>
                                ) : header.name === "Name" ? (
                                  <Mui.Tooltip
                                    title={eligible_employee?.employee_name}
                                    arrow
                                    slotProps={tooltipStyleProps}
                                  >
                                    <Mui.Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      maxWidth={120}
                                    >
                                      <Mui.Avatar
                                        alt={eligible_employee?.employee_name
                                          ?.charAt(0)
                                          .toUpperCase()}
                                        src={eligible_employee?.image_url}
                                        sx={{ width: 23, height: 23 }}
                                      />
                                      <span
                                        style={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "block",
                                        }}
                                      >
                                        {eligible_employee?.employee_name}
                                      </span>
                                    </Mui.Box>
                                  </Mui.Tooltip>
                                ) : eligible_employee[header.key] ||
                                  eligible_employee[header.key] === 0 ? (
                                  String(eligible_employee[header.key]).length >
                                  20 ? (
                                    <Mui.Tooltip
                                      title={eligible_employee[header.key]}
                                      arrow
                                      slotProps={tooltipStyleProps}
                                    >
                                      <span
                                        style={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          display: "inline-block",
                                          maxWidth: 80,
                                        }}
                                      >
                                        {eligible_employee[header.key]}
                                      </span>
                                    </Mui.Tooltip>
                                  ) : (
                                    eligible_employee[header.key]
                                  )
                                ) : (
                                  "N/A"
                                )}
                              </Mui.TableCell>
                            ) : null
                          )}
                        </Mui.TableRow>
                      </>
                    ))
                )}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
          <Mui.Grid
            container
            sx={{
              fisplay: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 0.2,
            }}
            spacing={2}
          >
            <Mui.Grid item>
              <CustomButtonWithOutLoader
                actionFuntion={() => {
                  handleBulkAdd();
                }}
                actionName="Add"
                typeName="submit"
                customWidth={true}
                disableAction={selectRemoveRows?.size}
                AddIcon={<ArrowUpward fontSize="small" />}
              />
            </Mui.Grid>
            <Mui.Grid item>
              <CustomButtonWithOutLoader
                actionFuntion={() => {
                  handleBulkRemove();
                }}
                disableAction={selectRestoreRows?.size}
                actionName="Remove"
                typeName="submit"
                customWidth={true}
                customTitleSx={{
                  backgroundColor: themes.redColor,
                  color: themes?.white,
                  "&:hover": {
                    backgroundColor: themes.redColor,
                  },
                }}
                AddIcon={<ArrowDownward fontSize="small" />}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid container sx={{ mr: 2, ml: 2 }}>
            <Mui.Grid item xs={12}>
              <Mui.Grid
                item
                sx={{
                  mt: 2,
                  border: `2px solid ${themes.sidebarDivider}`,
                  backgroundColor: formBackGroundColor,
                  padding: 1,
                  borderRadius: 1,
                }}
              >
                <Mui.Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
                  {`Incentive Removed Employees : ${removedEmployees?.length}`}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Grid>

          <Mui.TableContainer
            ref={containerRef}
            component={Mui.Paper}
            sx={{
              width: "100%",
              minHeight: 330,
              maxHeight: 330,
              ml: 2,
              mr: 2,
              mt: 2,
            }}
          >
            <Mui.Table className="border">
              <Mui.TableHead
                sx={{
                  backgroundColor: themes.primary,
                  position: "sticky",
                  top: -1,
                  zIndex: 10,
                }}
              >
                <Mui.TableRow sx={tableRowSx}>
                  {RemovedEmployeeHeader.map((header) => (
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        textAlign: header.name === "Action" ? "center" : "left",
                      }}
                    >
                      {header.key === "checkbox" ? (
                        <Mui.Checkbox
                          checked={restoreSelectAll}
                          onChange={(event) => {
                            if (
                              checkFirstLevelApproverPermission(
                                userScreenAccess?.module_details,
                                "Approve",
                                "Over Time"
                              )
                            ) {
                              handleRestore(event);
                            } else {
                              showToast(
                                "error",
                                "Access denied: You are not authorized to approve or  reject the overtime"
                              );
                            }
                          }}
                          size="medium"
                          // disabled={
                          //   eligibleMetEmployee?.employees?.length === 0 ||
                          //   eligibleMetEmployee?.employees?.length === 1
                          // }
                        />
                      ) : header.name !== "Arrow" ? (
                        header.name
                      ) : null}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {removedEmployees?.length === 0 ? (
                  <Mui.TableRow sx={tableRowSx}>
                    <Mui.TableCell colSpan={12}>
                      <Mui.Alert severity="info" sx={{ margin: 2 }}>
                        No data available
                      </Mui.Alert>
                    </Mui.TableCell>
                  </Mui.TableRow>
                ) : (
                  removedEmployees
                    // ?.slice(
                    //   listViewPage * rowsPerPage,
                    //   listViewPage * rowsPerPage + rowsPerPage
                    // )
                    .map((eligible_employee) => (
                      <>
                        <Mui.TableRow
                          key={eligible_employee?.user}
                          sx={tableRowSx}
                        >
                          {RemovedEmployeeHeader.map((header) =>
                            header.name === "" ? (
                              <Mui.TableCell sx={{ width: "1%" }}>
                                <Mui.Checkbox
                                  checked={selectRestoreRows.has(
                                    eligible_employee?.user
                                  )}
                                  onChange={() => {
                                    if (
                                      checkFirstLevelApproverPermission(
                                        userScreenAccess?.module_details,
                                        "Approve",
                                        "Over Time"
                                      )
                                    ) {
                                      handleRestoreRowSelect(
                                        eligible_employee?.user
                                      );
                                    } else {
                                      showToast(
                                        "error",
                                        "Access denied: You are not authorized to approve or  reject the overtime"
                                      );
                                    }
                                  }}
                                />
                              </Mui.TableCell>
                            ) : header.name !== "Action" &&
                              header.name !== "Arrow" ? (
                              <Mui.TableCell sx={{ color: themes.headLine }}>
                                {header.name === "Name" ? (
                                  <Mui.Tooltip
                                    title={eligible_employee?.employee_name}
                                    arrow
                                    slotProps={tooltipStyleProps}
                                  >
                                    <Mui.Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      maxWidth={120}
                                    >
                                      <Mui.Avatar
                                        alt={eligible_employee?.employee_name
                                          ?.charAt(0)
                                          .toUpperCase()}
                                        src={eligible_employee?.image_url}
                                        sx={{ width: 23, height: 23 }}
                                      />
                                      <span
                                        style={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "block",
                                        }}
                                      >
                                        {eligible_employee?.employee_name}
                                      </span>
                                    </Mui.Box>
                                  </Mui.Tooltip>
                                ) : eligible_employee[header.key] ||
                                  eligible_employee[header.key] === 0 ? (
                                  String(eligible_employee[header.key]).length >
                                  20 ? (
                                    <Mui.Tooltip
                                      title={eligible_employee[header.key]}
                                      arrow
                                      slotProps={tooltipStyleProps}
                                    >
                                      <span
                                        style={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          display: "inline-block",
                                          maxWidth: 80,
                                        }}
                                      >
                                        {eligible_employee[header.key]}
                                      </span>
                                    </Mui.Tooltip>
                                  ) : (
                                    eligible_employee[header.key]
                                  )
                                ) : (
                                  "N/A"
                                )}
                              </Mui.TableCell>
                            ) : null
                          )}
                        </Mui.TableRow>
                      </>
                    ))
                )}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
        </>
      )}
    </React.Fragment>
  );
};
